import * as React from 'react';
import { useEffect, useState } from 'react';
import { DropzoneOptions, useDropzone } from 'react-dropzone';
import { FileFormatEnum } from 'types/generated/client';
import AddPhotoV2 from 'svg/AddPhotoV2';
import CrossPhoto from 'svg/CrossPhoto';
import classNames from 'styles/utils/classNames';

const DEFAULT_MAX_FILE_SIZE = 20 * 1024 * 1024;
const toMb = (bytes: number) => Math.round(bytes / 1024 / 1024);

interface SelectedFile extends Partial<File> {
  url: string;
  fileFormat: FileFormatEnum;
}

interface Props extends DropzoneOptions {
  onSelectFiles(files: File[]): void;
  onRemoveFile?(file: SelectedFile, fileIndex: number): void;
  styles?: string;
  initialFiles?: SelectedFile[];
  uploading: boolean;
}

export default function MultiDropzone({
  onSelectFiles,
  onRemoveFile,
  styles = '',
  uploading = false,
  initialFiles = [],
  ...rest
}: Props) {
  const [selectedFiles, setSelectedFiles] = useState<SelectedFile[]>(initialFiles || []);

  useEffect(() => {
    if (selectedFiles.length === 0 && initialFiles && initialFiles.length > 0) {
      setSelectedFiles(initialFiles);
    }
  }, [selectedFiles, initialFiles]);

  const onDrop = async (acceptedFiles: File[]) => {
    const newFiles: SelectedFile[] = await Promise.all(
      acceptedFiles.map(async (file) => {
        const fileFormat = file.type.startsWith('video/')
          ? FileFormatEnum.Video
          : FileFormatEnum.Image;
        const url = URL.createObjectURL(file);
        return {
          ...file,
          fileName: file.name,
          url,
          fileFormat,
        };
      }),
    );

    setSelectedFiles((prevFiles) => [...prevFiles, ...newFiles]);
    onSelectFiles(acceptedFiles);
  };

  const handleRemoveFile = (file: SelectedFile, fileIndex: number) => {
    const updatedFiles = selectedFiles.filter((_, index) => index !== fileIndex);
    setSelectedFiles(updatedFiles);
    onRemoveFile?.(file, fileIndex);
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple: true,
    disabled: uploading,
    ...rest,
    maxSize: rest.maxSize || DEFAULT_MAX_FILE_SIZE,
  });

  return (
    <div className="flex w-full flex-col">
      <div
        {...getRootProps()}
        className={classNames(
          `flex w-full items-center justify-center bg-color-bg-lightmode-tertiary pb-8 pt-8 dark:bg-color-bg-darkmode-tertiary`,
          styles,
        )}
      >
        <input {...getInputProps()} />
        <div className="flex cursor-pointer flex-col items-center text-color-text-lightmode-placeholder dark:text-color-text-darkmode-placeholder">
          <AddPhotoV2 className="h-8 w-8 [&>path]:fill-color-text-lightmode-icon dark:[&>path]:fill-color-text-darkmode-icon" />
          <div>
            <span className="typography-product-body-highlight text-color-text-brand dark:text-color-text-brand">
              Click to upload{' '}
            </span>{' '}
            or drag and drop
          </div>
          <span>PNG, JPG, or MP4 (max. {toMb(rest.maxSize || DEFAULT_MAX_FILE_SIZE)} MB)</span>
        </div>
      </div>

      <div className="mt-2 flex flex-wrap gap-4">
        {selectedFiles.map((file, fileIndex) => (
          <div key={fileIndex} className="relative">
            {file.fileFormat === FileFormatEnum.Video ? (
              <video
                src={file.url}
                className="h-[7.8125rem] w-[7.8125rem] rounded-md border border-color-border-input-lightmode object-cover dark:border-color-border-input-darkmode"
                controls
                poster={file.url}
              />
            ) : (
              <img
                src={file.url}
                alt="Selected"
                className="h-[7.8125rem] w-[7.8125rem] rounded-md border border-color-border-input-lightmode object-cover object-top dark:border-color-border-input-darkmode"
              />
            )}
            <span
              className="absolute right-1 top-1 inline-flex cursor-pointer items-center justify-center rounded-full bg-color-bg-lightmode-primary p-1 hover:opacity-95 dark:bg-color-bg-darkmode-primary"
              onClick={() => handleRemoveFile(file, fileIndex)}
            >
              <CrossPhoto className="h-4 w-4" />
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
